/* Libraries Imports */
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { checkPermissionNotification, sendNotification } from '../../notification';
import { createSelector } from 'reselect';

/* UI Imports */
import Badge from '@material-ui/core/Badge';
import { IconMessage, IconMessageSelected } from '../IconSet';

import { usePrevious } from '../../lib/utils/hooks';
import { State } from '../../lib/reducers';
import { UserMessage, ChatMessages, UnreadPrivateUserMessages, LastMessage } from '../../lib/redux_types';
import { setChatOpen } from '../../lib/actions/room';


function ChatIcon(props: ExtendedProps) {
  const dispatch = useDispatch();
  // check browser notification permission at mount
  React.useEffect(() => checkPermissionNotification(), []);

  // send notification on chatMessages prop change
  const prevLastMessage = usePrevious(props.lastMessage, {} as LastMessage);
  React.useEffect(
    () => {
      if (!(document.hasFocus()) && prevLastMessage.msgId !== props.lastMessage.msgId) {
        if (props.lastMessage.from !== props.myself) {
          const title = props.lastMessage.displayName;
          const lastMessage = props.lastMessage.message;
          sendNotification(title, lastMessage);
        }
      }
    },
    [props.lastMessage, prevLastMessage, props.myself]
  );

  React.useEffect(
    () => {
      dispatch(setChatOpen(props.chatPanelOpen));
    }, [dispatch, props.chatPanelOpen]
  );

  const unreadMessageCount = props.unreadPublicMessages + props.unreadPrivateMessages;

  return (
    <Badge badgeContent={unreadMessageCount} color="secondary">
      { props.chatPanelOpen ? <IconMessageSelected size={28} /> : <IconMessage size={28}/> }
    </Badge>
  );
}

type Props = {
  chatPanelOpen: boolean;
}

type MappedProps = {
  publicMessages: UserMessage[];
  myself: null | string;
  unreadPublicMessages: number;
  unreadPrivateMessages: number;
  lastMessage: LastMessage;
}

type ExtendedProps = Props & MappedProps;

const getPublicMessages = createSelector(
  (state: State) => state.room.publicMessages,
  _getPublicMessages
);

const getUnreadPrivateMessageCount = createSelector(
  (state: State) => state.room.unreadPrivateMessages,
  _getUnreadPrivateMessageCount
);

const mapStateToProps = (state: State): MappedProps => ({
  publicMessages: getPublicMessages(state),
  myself: state.websocket.uid,
  unreadPublicMessages: state.room.unreadPublicMessages,
  unreadPrivateMessages: getUnreadPrivateMessageCount(state),
  lastMessage: state.room.lastMessage,
});


export default connect(mapStateToProps)(ChatIcon);

function _getPublicMessages(allMessages: ChatMessages): UserMessage[] {
  return allMessages.filter((msg) => msg.class !== 'event') as UserMessage[];
}

function _getUnreadPrivateMessageCount(allMessages: UnreadPrivateUserMessages): number {
  let count = 0;
  Object.values(allMessages).forEach((n) => count += n);
  return count;
}
